/** Client-safe Google / Meta audience CSV column catalog (headers, types, example row). */

export type SsdExportDestination = "google" | "meta";

export type SsdExportColumnPreview = {
  header: string;
  type: "string" | "number";
  note: string;
  example: string;
};

const HASHED = "SHA-256 hex string";

/** Google Ads offline-conversion preamble. Lives in A1, above the header row. */
export const GOOGLE_ADS_TIMEZONE_PARAM = "Parameters:TimeZone=America/New_York";

/** Google Ads conversion action for ShedSuite / Turbo Assist offline sales. */
export const GOOGLE_ADS_CONVERSION_NAME_DEFAULT = "Offline Shed Sales";

/** Previous default; kept as a saved option until the org removes it. */
export const GOOGLE_ADS_CONVERSION_NAME_LEGACY = "Submit for Quote:ShedCor";

export const GOOGLE_EXPORT_COLUMNS: SsdExportColumnPreview[] = [
  { header: "Email", type: "string", note: HASHED, example: "8c87b489ce35cf2e2f39f80e282cb2e8…" },
  { header: "Phone", type: "string", note: HASHED, example: "b4372da4002225d8ce5d2796ac2047b0…" },
  { header: "First Name", type: "string", note: HASHED, example: "81f8f6dde88365f3928796ec7aa53f72…" },
  { header: "Last Name", type: "string", note: HASHED, example: "799ef92a11af918e3fb741df42934f3b…" },
  { header: "Country", type: "string", note: "ISO-2 text", example: "US" },
  { header: "Zip", type: "string", note: "5-digit ZIP", example: "29526" },
  {
    header: "Conversion Name",
    type: "string",
    note: "Google Ads conversion action",
    example: GOOGLE_ADS_CONVERSION_NAME_DEFAULT,
  },
  { header: "Conversion Time", type: "string", note: "yyyy-mm-dd HH:mm:ss", example: "2026-08-25 12:42:00" },
  { header: "Conversion Value", type: "number", note: "No $ or commas", example: "-322" },
  {
    header: "Event ID",
    type: "string",
    note: "ShedSuite Customer Order ID; map to Google Order ID to dedupe scheduled pulls",
    example: "528414",
  },
  {
    header: "Order ID",
    type: "string",
    note: "Google Ads transaction id; same value as Event ID",
    example: "528414",
  },
];

export const META_EXPORT_COLUMNS: SsdExportColumnPreview[] = [
  { header: "email", type: "string", note: HASHED, example: "8c87b489ce35cf2e2f39f80e282cb2e8…" },
  { header: "phone", type: "string", note: HASHED, example: "b4372da4002225d8ce5d2796ac2047b0…" },
  { header: "fn", type: "string", note: HASHED, example: "81f8f6dde88365f3928796ec7aa53f72…" },
  { header: "ln", type: "string", note: HASHED, example: "799ef92a11af918e3fb741df42934f3b…" },
  { header: "country", type: "string", note: "ISO-2 text", example: "US" },
  { header: "zip", type: "string", note: "5-digit ZIP", example: "29526" },
  { header: "ct", type: "string", note: "City text", example: "Conway" },
  { header: "st", type: "string", note: "State text", example: "SC" },
  { header: "event_time", type: "string", note: "yyyy-mm-dd HH:mm:ss", example: "2026-08-25 12:42:00" },
  { header: "value", type: "number", note: "No $ or commas", example: "-322" },
  { header: "currency", type: "string", note: "ISO currency", example: "USD" },
  {
    header: "event_id",
    type: "string",
    note: "ShedSuite Customer Order ID; Meta CAPI dedup key",
    example: "528414",
  },
];

export function audienceExportColumns(
  destination: SsdExportDestination,
  conversionName?: string
): SsdExportColumnPreview[] {
  if (destination !== "google") return META_EXPORT_COLUMNS;
  const name = (conversionName ?? GOOGLE_ADS_CONVERSION_NAME_DEFAULT).trim() || GOOGLE_ADS_CONVERSION_NAME_DEFAULT;
  return GOOGLE_EXPORT_COLUMNS.map((col) =>
    col.header === "Conversion Name" ? { ...col, example: name } : col
  );
}

export function audienceExportHeaders(destination: SsdExportDestination): string[] {
  return audienceExportColumns(destination).map((c) => c.header);
}

export function audienceExportExampleRow(destination: SsdExportDestination): string[] {
  return audienceExportColumns(destination).map((c) => c.example);
}
